/**
 * Rules and copy for the dashboard "Queue job" dialog.
 *
 * Everything here mirrors `POST /api/jobs`
 * (`apps/api/flow_api/routers/jobs.py::queue_job`). Nothing in this
 * file is a frontend-only rule: if the API changes, change it here.
 */
import { z } from "zod";

import { ApiError } from "@/lib/apiClient";
import type { Workflow } from "@/features/workflows/api";

/**
 * Maturity states the API accepts. Any other state is refused with
 * 409 `workflow_not_queueable` (today: `paused` and `retired`).
 */
export const QUEUEABLE_MATURITY_STATES: readonly string[] = [
  "active",
  "breaking_in",
  "testing",
  "draft",
];

/**
 * Job types whose inputs the API validates against a typed schema
 * (`AuthorizationCheckInput`: patient, rendering provider, body part,
 * CPT codes, date of service). This dialog has no form for them, so
 * these workflows are not offered here. They are queued by the
 * authorization-check worklist.
 */
export const TYPED_INPUT_WORKFLOW_TYPES: readonly string[] = ["managed_care_auth_check"];

/**
 * Workflow types the API only queues as a full run
 * (`FULL_RUN_ONLY_WORKFLOW_TYPES` in jobs.py; a smoke request is
 * refused with 409 `workflow_requires_full_run`). A smoke run skips a
 * workflow's phases, and the whole point of these is in their phases:
 * `supervised_test` is `__live_view_test`, which pauses for a person;
 * `portal_sign_in_test` is `medpoint_sign_in_test`, which signs in to a
 * portal. (The API also refuses a smoke run of any workflow whose
 * definition declares a portal login; the workflow list does not carry
 * the definition, so that case shows the 409 message below.)
 */
export const FULL_RUN_ONLY_WORKFLOW_TYPES: readonly string[] = [
  "supervised_test",
  "portal_sign_in_test",
];

/** The run type a workflow must be queued with, or null when either works. */
export function requiredMode(workflow: Workflow | undefined): "full" | null {
  return workflow && FULL_RUN_ONLY_WORKFLOW_TYPES.includes(workflow.workflowType)
    ? "full"
    : null;
}

export type Availability =
  | { ok: true }
  | { ok: false; reason: "not_queueable" | "needs_inputs"; note: string };

export function maturityLabel(state: string): string {
  const text = state.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function availability(workflow: Workflow): Availability {
  if (!QUEUEABLE_MATURITY_STATES.includes(workflow.maturityState)) {
    return {
      ok: false,
      reason: "not_queueable",
      note: `${maturityLabel(workflow.maturityState)} workflows cannot be queued`,
    };
  }
  if (TYPED_INPUT_WORKFLOW_TYPES.includes(workflow.workflowType)) {
    return {
      ok: false,
      reason: "needs_inputs",
      note: "needs patient and authorization inputs, not available here",
    };
  }
  return { ok: true };
}

/** Option text: name, portal (the workflow type), maturity state. */
export function workflowOptionLabel(workflow: Workflow): string {
  const base = `${workflow.name} · ${workflow.workflowType} · ${maturityLabel(workflow.maturityState)}`;
  const a = availability(workflow);
  return a.ok ? base : `${base} (${a.note})`;
}

export const queueJobSchema = z.object({
  entityCode: z.string().min(1, "Select an entity."),
  workflowId: z.string().min(1, "Select a workflow."),
  mode: z.enum(["smoke", "full"]),
});

export type QueueJobValues = z.infer<typeof queueJobSchema>;

export interface QueueErrorView {
  kind: "error" | "denied";
  message: string;
  correlationId: string | null;
}

export const QUEUE_ROLES_HINT =
  "Queuing jobs needs the operator, supervisor or entity admin role. Ask your entity admin or a Flow global admin.";

/** Human-readable message for a failed `POST /api/jobs`. */
export function describeQueueError(error: unknown, workflow?: Workflow): QueueErrorView {
  if (!(error instanceof ApiError)) {
    const signedOut = error instanceof Error && error.message === "not_signed_in";
    return {
      kind: "error",
      message: signedOut
        ? "Your session has ended. Sign in again, then retry."
        : "Could not reach the Flow API. Check your connection and try again.",
      correlationId: null,
    };
  }

  const view = (message: string, kind: QueueErrorView["kind"] = "error"): QueueErrorView => ({
    kind,
    message,
    correlationId: error.correlationId,
  });

  switch (error.status) {
    case 401:
      return view("Your session has ended. Sign in again, then retry.");
    case 403:
      return view(`You don't have access to queue jobs. ${QUEUE_ROLES_HINT}`, "denied");
    case 404:
      return view(
        error.message === "entity_not_found"
          ? "Entity not found, or you don't have access to it."
          : "Workflow not found for this entity. It may have been removed. Close this dialog and try again.",
      );
    case 409:
      if (error.message === "workflow_requires_full_run") {
        return view("This workflow only runs as a Full run. Choose Full as the run type.");
      }
      if (error.message === "workflow_has_no_phases") {
        return view("This workflow has no phases defined, so there is nothing to run.");
      }
      return view(
        workflow
          ? `This workflow is ${maturityLabel(workflow.maturityState).toLowerCase()} and cannot be queued.`
          : "This workflow cannot be queued in its current state.",
      );
    case 422:
      return view("This workflow needs inputs that this dialog cannot collect. No job was queued.");
    case 429:
      return view("Too many jobs queued in a short time. Wait a minute, then try again.");
    case 502:
      return view(
        "The job was created but could not be handed to a runner. It is marked failed. Try again.",
      );
    default:
      return view("The job could not be queued. Try again.");
  }
}

/** Human-readable message for a failed workflow list load. */
export function describeLoadError(error: unknown): QueueErrorView {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return {
        kind: "denied",
        message: `You don't have access to workflows. ${QUEUE_ROLES_HINT}`,
        correlationId: error.correlationId,
      };
    }
    return {
      kind: "error",
      message:
        error.status === 429
          ? "Too many requests. Wait a minute, then retry."
          : "Workflows could not be loaded.",
      correlationId: error.correlationId,
    };
  }
  return {
    kind: "error",
    message: "Could not reach the Flow API. Check your connection and retry.",
    correlationId: null,
  };
}
